type Interval = {
    start: number;
    end: number;
};

export function getDeduplicatedDurationMinutes(intervals: Interval[]): number {
    const sortedIntervals = intervals
        .filter(interval => interval.end > interval.start)
        .sort((a, b) => a.start - b.start);

    if (sortedIntervals.length === 0) return 0;

    let totalDuration = 0;
    let currentStart = sortedIntervals[0].start;
    let currentEnd = sortedIntervals[0].end;

    for (const interval of sortedIntervals.slice(1)) {
        if (interval.start <= currentEnd) {
            currentEnd = Math.max(currentEnd, interval.end);
            continue;
        }

        totalDuration += currentEnd - currentStart;
        currentStart = interval.start;
        currentEnd = interval.end;
    }

    return (totalDuration + currentEnd - currentStart) / 60000;
}

const BREAK_THRESHOLD_30_MINUTES = 6 * 60 + 15; // 6:15h
const BREAK_THRESHOLD_45_MINUTES = 9 * 60 + 15; // 9:15h

/**
 * Statutory break deduction based on the availability duration of a single day.
 * From 9:15h onwards 45 min are deducted, above 6:15h 30 min are deducted.
 */
export function getDailyBreakDeductionMinutes(dailyAvailabilityMinutes: number): number {
    if (dailyAvailabilityMinutes >= BREAK_THRESHOLD_45_MINUTES) return 45;
    if (dailyAvailabilityMinutes > BREAK_THRESHOLD_30_MINUTES) return 30;
    return 0;
}

export function getDailyAvailabilityMinutes(
    availabilities: { startTime: string; endTime: string }[]
): number {
    const intervals = availabilities.map(availability => {
        const [startHour, startMinute] = availability.startTime.split(':').map(Number);
        const [endHour, endMinute] = availability.endTime.split(':').map(Number);
        const start = (startHour * 60 + startMinute) * 60000;
        let end = (endHour * 60 + endMinute) * 60000;

        if (end <= start) end += 24 * 60 * 60000;

        return { start, end };
    });

    return getDeduplicatedDurationMinutes(intervals);
}

/**
 * Net daily availability after subtracting the statutory break deduction.
 */
export function getDailyNetAvailabilityMinutes(
    availabilities: { startTime: string; endTime: string }[]
): number {
    const dailyMinutes = getDailyAvailabilityMinutes(availabilities);
    return Math.max(dailyMinutes - getDailyBreakDeductionMinutes(dailyMinutes), 0);
}

export function getWeeklyAvailabilityMinutes(
    days: { availabilities: { startTime: string; endTime: string }[] }[]
): number {
    return days.reduce(
        (weeklyTotal, day) => weeklyTotal + getDailyNetAvailabilityMinutes(day.availabilities),
        0
    );
}

export function formatAvailabilityHours(minutes: number): string {
    return new Intl.NumberFormat('de-DE', {
        maximumFractionDigits: 2,
    }).format(minutes / 60);
}
